import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { MenuVisibility as MenuVisibilityType, platformSettingsAPI, featureFlagsAPI, type FeatureFlags, type DashboardTabsVisibility, type CustomerCardFieldsVisibility, type DataImportTilesVisibility, type CustomerProfileLayout, type HelpLayout } from '../lib/api';
import { applyHierarchyLabels } from '../components/orgchart/types';

// Re-export MenuVisibility for convenience
export type MenuVisibility = MenuVisibilityType;

// Portal type values: '1' = Supplier, '2' = Customer
export type MainMenuType = '1' | '2';

// Portal mode: 'standard' = Helix default, 'compliance' = Compliance module,
// 'supplier_portal' = Customer is platform supplier
export type PortalMode = 'standard' | 'compliance' | 'supplier_portal';

interface MenuSettingsContextType {
  menuVisibility: MenuVisibility;
  updateMenuVisibility: (settings: MenuVisibility) => void;
  isMenuItemVisible: (key: keyof MenuVisibility) => boolean;
  loadSettingsFromBackend: () => Promise<void>;
  isLoading: boolean;
  // Platform-wide portal type setting (controls default portal experience)
  mainMenuType: MainMenuType;
  // Helper to check if the platform default is supplier portal
  isSupplierPortalDefault: boolean;
  // Helper to check if the platform default is customer portal
  isCustomerPortalDefault: boolean;
  // Feature flags
  featureFlags: FeatureFlags;
  isFeatureEnabled: (feature: Exclude<keyof FeatureFlags, 'portal_mode' | 'dashboard_tabs' | 'customer_dashboard_tabs'>) => boolean;
  // Portal mode - determines navigation structure
  portalMode: PortalMode;
  isComplianceMode: boolean;
  isSupplierPortalMode: boolean;
  // Dashboard tab visibility (CR-4441)
  dashboardTabs: DashboardTabsVisibility;
  customerDashboardTabs: DashboardTabsVisibility;
  isDashboardTabEnabled: (tabId: string, isCustomer?: boolean) => boolean;
  updateDashboardTabs: (tabs: DashboardTabsVisibility) => void;
  updateCustomerDashboardTabs: (tabs: DashboardTabsVisibility) => void;
  // Customer card field visibility
  customerCardFields: CustomerCardFieldsVisibility;
  isCustomerCardFieldEnabled: (fieldId: keyof CustomerCardFieldsVisibility) => boolean;
  updateCustomerCardFields: (fields: CustomerCardFieldsVisibility) => void;
  // Data Import tile visibility
  dataImportTiles: DataImportTilesVisibility;
  isDataImportTileEnabled: (tileId: keyof DataImportTilesVisibility) => boolean;
  updateDataImportTiles: (tiles: DataImportTilesVisibility) => void;
  // Default landing path for approved customer users (pre-approval customers are
  // always routed to /onboarding/my-progress).
  customerDefaultLandingPath: string;
  updateCustomerDefaultLandingPath: (path: string) => void;
  customerProfileLayout: CustomerProfileLayout;
  updateCustomerProfileLayout: (layout: CustomerProfileLayout) => void;
  helpLayout: HelpLayout;
  updateHelpLayout: (layout: HelpLayout) => void;
  // Quality measure toggle: false = PPM (default), true = PPK.
  // Drives defect-rate labels across dashboards, settings, drill-downs.
  qualityMeasurePpk: boolean;
  // When true, the PO-line Communication tab/box is hidden across the portal
  // (Admin Setup → Open Orders visibility → Hide Communication Box).
  hideCommunicationBox: boolean;
}

const MenuSettingsContext = createContext<MenuSettingsContextType | undefined>(undefined);

export const DEFAULT_MENU_VISIBILITY: MenuVisibility = {
  // Supplier Menu Items - All enabled by default
  dashboard: true,
  customerPerformance: true,
  sitePerformance: true,
  exchangeRates: true,
  calendar: true,
  purchasing: true,
  rfq: true,
  quality: true,
  concessions: true,
  customers: true,
  documents: true,
  customerIntelligence: true,
  customerOnboarding: true,
  userInvitations: true,
  userActivityHistory: true,
  questionnaire: true,
  newsRelease: true,
  adminSetup: true,
  partsMaster: true,
  partSchedule: false,  // Off by default — opt-in per install
  analytics: true,  // Default true so existing installs don't silently lose the Analytics sidebar item
  dataImport: true,
  // Customer Menu Items - All enabled by default
  purchaseOrders: true,
  companyProfile: true,
  suppliers: true,  // Suppliers list for customer portal
  callOff: true,     // Call Off tab in Purchasing
  customerDashboard: true,
  customerQuestionnaires: true,
  customerCalendar: true,
  customerSuppliers: true,
  customerDocuments: true,
  customerNewsRelease: true,
};

const DEFAULT_DATA_IMPORT_TILES: DataImportTilesVisibility = {
  commodity: true,
  faultcodes: true,
  departments: true,
  warehouses: true,
  customers: true,
  customer_additional: true,
  grns: true,
  pos: true,
  ncrs: true,
  forecasts: true,
  users: true,
};

const DEFAULT_DASHBOARD_TABS: DashboardTabsVisibility = {
  'default': false,
  'scheduled': false,
  'spot-orders': false,
  'sales-orders': true,
  'forecast-forward': true,
  'cached-performance': false,
  'group-kpi': false,
  'group-detail-kpi': false,
  'site-kpi': false,
  'cost': false,
  'emissions': false,
  'compliance': false,
  'future-forecast': false,
  'date-adherence': false,
  'expediting': false,
  'performance-ratings': false,
};

const DEFAULT_CUSTOMER_DASHBOARD_TABS: DashboardTabsVisibility = {
  'default': false,
  'scheduled': false,
  'spot-orders': false,
  'sales-orders': true,
  'forecast-forward': false,
  'cached-performance': false,
  'group-kpi': false,
  'group-detail-kpi': false,
  'site-kpi': false,
  'cost': true,
  'emissions': false,
  'compliance': true,
  'future-forecast': false,
  'date-adherence': true,
  'expediting': false,
  'performance-ratings': true,
};

const DEFAULT_CUSTOMER_CARD_FIELDS: CustomerCardFieldsVisibility = {
  logo: true,
  risk_badge: true,
  otd: true,
  rating: true,
  questionnaire_status: true,
};

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  delivery_appeals: false,
  spend_leakage: false,
  bulk_upload: false,
  commodity_codes: false,
  customer_stock_levels: false,
  switch_call_off_tab: false,
  allow_customer_decline: false,
  po_line_schedules: false,
  schedule_collaboration: false,
  show_forecast_total: false,
  sales_orders_otif: false,
  // Certified paperwork sent to customers — off until switched on.
  job_documents: false,
  portal_mode: 'standard',
};

/**
 * Provides platform-wide menu visibility, feature flags, and portal mode settings.
 * Loads feature flags for all authenticated users and full platform settings for admins only.
 */
export const MenuSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Start with defaults - will be replaced by backend data
  const [menuVisibility, setMenuVisibility] = useState<MenuVisibility>(DEFAULT_MENU_VISIBILITY);
  const [isLoading, setIsLoading] = useState(true);
  // Platform-wide main menu type: '1' = Supplier Portal, '2' = Customer Portal (default)
  const [mainMenuType, setMainMenuType] = useState<MainMenuType>('2');
  // Feature flags
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  // Dashboard tab visibility (CR-4441)
  const [dashboardTabs, setDashboardTabs] = useState<DashboardTabsVisibility>(DEFAULT_DASHBOARD_TABS);
  const [customerDashboardTabs, setCustomerDashboardTabs] = useState<DashboardTabsVisibility>(DEFAULT_CUSTOMER_DASHBOARD_TABS);
  // Customer card field visibility
  const [customerCardFields, setCustomerCardFields] = useState<CustomerCardFieldsVisibility>(DEFAULT_CUSTOMER_CARD_FIELDS);
  // Data Import tile visibility
  const [dataImportTiles, setDataImportTiles] = useState<DataImportTilesVisibility>(DEFAULT_DATA_IMPORT_TILES);
  // Customer default landing path
  const [customerDefaultLandingPath, setCustomerDefaultLandingPath] = useState<string>('/dashboard');
  // Customer Profile page layout (which stats/tabs/widgets to show)
  const [customerProfileLayout, setCustomerProfileLayout] = useState<CustomerProfileLayout>({
    stats: { otd: true, total_orders: true },
    tabs: {
      performance: true, orders: true, contracts: true, quality: true,
      questionnaires: true, credit_scores: true, users: true,
      activity_log: true, group_structure: true,
    },
    overview: { performance_summary: true, capabilities: true },
  });
  // Quality measure toggle: false = PPM (default, legacy), true = PPK.
  const [qualityMeasurePpk, setQualityMeasurePpk] = useState<boolean>(false);
  const [hideCommunicationBox, setHideCommunicationBox] = useState<boolean>(false);
  // Help & Support page layout — which FAQ categories and sidebar cards
  // a supplier wants visible. All on by default; superuser toggles from admin.
  const [helpLayout, setHelpLayout] = useState<HelpLayout>({
    faq: {
      getting_started: true,
      purchasing: true,
      customer_management: true,
      documents_communication: true,
    },
    sidebar: { tour: true, contact_support: true, system_status: true },
  });

  // Function to load settings from backend
  const loadSettingsFromBackend = useCallback(async () => {
    // Only fetch if user is authenticated (token exists)
    // Check localStorage and sessionStorage for token (key may be in either)
    const token = localStorage.getItem('helix_auth_token') || sessionStorage.getItem('helix_auth_token') || localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // First, load feature flags for ALL authenticated users
    try {
      const flagsResponse = await featureFlagsAPI.getFeatureFlags();
      if (flagsResponse.data) {
        setFeatureFlags({
          ...DEFAULT_FEATURE_FLAGS,
          ...flagsResponse.data,
        });
        // CR-4441: Load dashboard tab visibility from feature flags (available to all users)
        if (flagsResponse.data.dashboard_tabs && typeof flagsResponse.data.dashboard_tabs === 'object') {
          setDashboardTabs({
            ...DEFAULT_DASHBOARD_TABS,
            ...flagsResponse.data.dashboard_tabs,
          });
        }
        if (flagsResponse.data.customer_dashboard_tabs && typeof flagsResponse.data.customer_dashboard_tabs === 'object') {
          setCustomerDashboardTabs({
            ...DEFAULT_CUSTOMER_DASHBOARD_TABS,
            ...flagsResponse.data.customer_dashboard_tabs,
          });
        }
      }
    } catch {
      // Feature flags endpoint might not exist or be accessible
      // Keep using defaults
    }

    // Load platform display settings for ALL authenticated users
    // These are platform-wide settings (tab visibility, feature flags, menu config)
    try {
      const response = await platformSettingsAPI.getDisplaySettings();
      if (response.data) {
        // Load menu visibility settings
        if (response.data.menu_visibility) {
          setMenuVisibility({
            ...DEFAULT_MENU_VISIBILITY,
            ...response.data.menu_visibility,
          });
        }
        // Load main_menu_type (portal type setting)
        if (response.data.main_menu_type) {
          const menuType = response.data.main_menu_type;
          if (menuType === '1' || menuType === '2') {
            setMainMenuType(menuType);
          }
        }
        // Load feature flags
        if (response.data.feature_flags) {
          setFeatureFlags({
            ...DEFAULT_FEATURE_FLAGS,
            ...response.data.feature_flags,
          });
        }
        // Load dashboard tab visibility (CR-4441)
        if (response.data.dashboard_tabs) {
          setDashboardTabs({
            ...DEFAULT_DASHBOARD_TABS,
            ...response.data.dashboard_tabs,
          });
        }
        if (response.data.customer_dashboard_tabs) {
          setCustomerDashboardTabs({
            ...DEFAULT_CUSTOMER_DASHBOARD_TABS,
            ...response.data.customer_dashboard_tabs,
          });
        }
        // Load customer card field visibility
        const cardFields = (response.data as Record<string, unknown>)['customer_card_fields'] as Record<string, boolean> | undefined;
        if (cardFields) {
          setCustomerCardFields({
            ...DEFAULT_CUSTOMER_CARD_FIELDS,
            ...cardFields,
          });
        }
        // Load Data Import tile visibility
        const importTiles = (response.data as Record<string, unknown>)['data_import_tiles'] as Record<string, boolean> | undefined;
        if (importTiles) {
          setDataImportTiles({
            ...DEFAULT_DATA_IMPORT_TILES,
            ...importTiles,
          });
        }
        // Load customer default landing path
        const landing = (response.data as Record<string, unknown>)['customer_default_landing_path'] as string | undefined;
        if (landing) {
          setCustomerDefaultLandingPath(landing);
        }
        // Load customer profile layout
        const layout = (response.data as Record<string, unknown>)['customer_profile_layout'] as CustomerProfileLayout | undefined;
        if (layout && typeof layout === 'object' && layout.tabs) {
          setCustomerProfileLayout(prev => ({
            stats: { ...prev.stats, ...(layout.stats || {}) },
            tabs: { ...prev.tabs, ...(layout.tabs || {}) },
            overview: { ...prev.overview, ...(layout.overview || {}) },
          }));
        }
        // Load quality measure toggle (PPM vs PPK). Falls back to false (PPM).
        const qmp = (response.data as Record<string, unknown>)['quality_measure_ppk'];
        setQualityMeasurePpk(typeof qmp === 'boolean' ? qmp : false);
        // Load Open Orders visibility -> Hide Communication Box toggle.
        const oov = (response.data as Record<string, unknown>)['open_orders_visibility'] as
          { hide_communication_box?: boolean } | undefined;
        setHideCommunicationBox(!!(oov && oov.hide_communication_box));
        // Load Help & Support page layout
        const help = (response.data as Record<string, unknown>)['help_layout'] as HelpLayout | undefined;
        if (help && typeof help === 'object') {
          setHelpLayout(prev => ({
            faq: { ...prev.faq, ...(help.faq || {}) },
            sidebar: { ...prev.sidebar, ...(help.sidebar || {}) },
          }));
        }
        // Apply configurable hierarchy labels platform-wide
        const labels = (response.data as Record<string, unknown>)['hierarchy_labels'] as { group?: string; division?: string; site?: string } | undefined;
        if (labels) {
          applyHierarchyLabels({
            group: labels.group || 'Group',
            division: labels.division || 'Division',
            site: labels.site || 'Company',
          });
        }
      }
    } catch (error) {
      console.error('Failed to load platform display settings:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load settings from backend on initial mount if authenticated
  useEffect(() => {
    loadSettingsFromBackend();
  }, [loadSettingsFromBackend]);

  const updateMenuVisibility = (settings: MenuVisibility) => {
    setMenuVisibility(settings);
  };

  const isMenuItemVisible = (key: keyof MenuVisibility): boolean => {
    // Fall back to DEFAULT_MENU_VISIBILITY rather than an unconditional `true`
    // so items that default to hidden (e.g. partSchedule) remain hidden when
    // the backend's stored menu_visibility doesn't explicitly include the key.
    // If the key is unknown to defaults, keep the legacy behaviour of
    // treating it as visible.
    return menuVisibility[key] ?? DEFAULT_MENU_VISIBILITY[key] ?? true;
  };

  // Derived helpers for portal type
  const isSupplierPortalDefault = mainMenuType === '1';
  const isCustomerPortalDefault = mainMenuType === '2';

  // Helper to check if a feature flag is enabled (only for boolean flags, not portal_mode)
  type BooleanFeatureFlags = Exclude<keyof FeatureFlags, 'portal_mode' | 'dashboard_tabs' | 'customer_dashboard_tabs'>;
  const isFeatureEnabled = (feature: BooleanFeatureFlags): boolean => {
    return featureFlags[feature] ?? false; // Default to disabled if not set
  };

  // Portal mode - determines navigation structure
  const portalMode: PortalMode = featureFlags.portal_mode || 'standard';
  const isComplianceMode = portalMode === 'compliance';
  const isSupplierPortalMode = portalMode === 'supplier_portal';

  // Helper to check if a dashboard tab is enabled (CR-4441).
  // Tabs added after CR-4441 default to OFF for existing tenants whose
  // PlatformSettings.dashboard_tabs JSON predates the new keys, so they
  // stay hidden until a tenant admin opts in via /admin → Dashboard Tabs.
  const isDashboardTabEnabled = (tabId: string, isCustomer?: boolean): boolean => {
    if (tabId === 'sales-orders') {
      const tabs = isCustomer ? customerDashboardTabs : dashboardTabs;
      return tabs[tabId] ?? true;
    }
    if (tabId === 'forecast-forward') {
      if (isCustomer) return false;
      return dashboardTabs[tabId] ?? true;
    }
    return false;
  };

  // Direct update for dashboard tab visibility (CR-4441)
  const updateDashboardTabs = useCallback((tabs: DashboardTabsVisibility) => {
    setDashboardTabs({ ...DEFAULT_DASHBOARD_TABS, ...tabs });
  }, []);

  const updateCustomerDashboardTabs = useCallback((tabs: DashboardTabsVisibility) => {
    setCustomerDashboardTabs({ ...DEFAULT_CUSTOMER_DASHBOARD_TABS, ...tabs });
  }, []);

  const isCustomerCardFieldEnabled = (fieldId: keyof CustomerCardFieldsVisibility): boolean => {
    return customerCardFields[fieldId] ?? true;
  };

  const updateCustomerCardFields = useCallback((fields: CustomerCardFieldsVisibility) => {
    setCustomerCardFields({ ...DEFAULT_CUSTOMER_CARD_FIELDS, ...fields });
  }, []);

  const isDataImportTileEnabled = (tileId: keyof DataImportTilesVisibility): boolean => {
    return dataImportTiles[tileId] ?? true;
  };

  const updateDataImportTiles = useCallback((tiles: DataImportTilesVisibility) => {
    setDataImportTiles({ ...DEFAULT_DATA_IMPORT_TILES, ...tiles });
  }, []);

  const updateCustomerDefaultLandingPath = useCallback((path: string) => {
    setCustomerDefaultLandingPath(path || '/dashboard');
  }, []);

  const updateCustomerProfileLayout = useCallback((layout: CustomerProfileLayout) => {
    setCustomerProfileLayout(layout);
  }, []);

  const updateHelpLayout = useCallback((layout: HelpLayout) => {
    setHelpLayout(layout);
  }, []);

  return (
    <MenuSettingsContext.Provider
      value={{
        menuVisibility,
        updateMenuVisibility,
        isMenuItemVisible,
        loadSettingsFromBackend,
        isLoading,
        mainMenuType,
        isSupplierPortalDefault,
        isCustomerPortalDefault,
        featureFlags,
        isFeatureEnabled,
        portalMode,
        isComplianceMode,
        isSupplierPortalMode,
        dashboardTabs,
        customerDashboardTabs,
        isDashboardTabEnabled,
        updateDashboardTabs,
        updateCustomerDashboardTabs,
        customerCardFields,
        isCustomerCardFieldEnabled,
        updateCustomerCardFields,
        dataImportTiles,
        isDataImportTileEnabled,
        updateDataImportTiles,
        customerDefaultLandingPath,
        updateCustomerDefaultLandingPath,
        customerProfileLayout,
        updateCustomerProfileLayout,
        helpLayout,
        updateHelpLayout,
        qualityMeasurePpk,
        hideCommunicationBox,
      }}
    >
      {children}
    </MenuSettingsContext.Provider>
  );
};

/**
 * Accesses menu visibility settings, feature flags, and portal mode configuration.
 * Must be used within a {@link MenuSettingsProvider}.
 *
 * @returns Menu settings with visibility checks, feature flag helpers, and portal mode state
 */
export const useMenuSettings = (): MenuSettingsContextType => {
  const context = useContext(MenuSettingsContext);
  if (!context) {
    throw new Error('useMenuSettings must be used within a MenuSettingsProvider');
  }
  return context;
};
